import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, GetApp, ArrowBack } from '@mui/icons-material';
import { IconButton, Tooltip, Snackbar, Alert } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';

const PRIMARY = '#0d47a1';

const fmt2Date = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const getDefaultFromDate = () => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
};

const fmtAmt = (n) =>
  (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const iconBtn = {
  color: 'rgba(255,255,255,0.85)',
  '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.15)' },
  borderRadius: '6px', p: '4px',
};

const FundFlowStatement = () => {
  useSessionValidation();
  const { companyId, token, companyName, username } = useAuth();
  const { logAudit } = useAuditLog();
  const history = useHistory();

  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState(fmt2Date(new Date()));
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('0');

  useEffect(() => {
    if (!companyId || !token) return;
    fetchData('BranchApi', `/${companyId}`, token)
      .then(res => {
        if (res.responseCode === '1111') {
          setBranches(res.response_data || []);
        } else {
          setBranches(res.response_data || res || []);
        }
      })
      .catch(() => setBranches([]));
  }, [companyId, token]);

  useEffect(() => {
    if (companyId && token && fromDate && toDate) {
        if (new Date(fromDate) > new Date(toDate)) {
            setData([]);
            setError('From Date cannot be greater than To Date');
            return;
        }
        fetchReport();
    }
  }, [companyId, token, fromDate, toDate, selectedBranch]);

  const fetchReport = async () => {
    if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
    setLoading(true);
    try {
      const branchParam = selectedBranch === '0' ? 0 : parseInt(selectedBranch);
      const res = await fetchData(
        'MISReportApi',
        `/FundFlowReport/${fromDate}/${toDate}/${companyId}/${branchParam}`,
        token
      );
      const rawData = res.response_data || res || [];
      setData(rawData);
    } catch (e) {
      console.error(e);
      setError('Failed to load fund flow data');
    } finally {
      setLoading(false);
    }
  };

  // Type: "Source" / "Application"
  const sources = data
    .filter(r => (r.Type || '').toLowerCase() === 'source')
    .sort((a, b) => (a.SortOrder || 0) - (b.SortOrder || 0));

  const applications = data
    .filter(r => (r.Type || '').toLowerCase() === 'application')
    .sort((a, b) => (a.SortOrder || 0) - (b.SortOrder || 0));

  // Detect header rows: a row is a header if the next row's Particular
  // starts with this row's Particular (i.e., it has sub-items)
  const isHeaderRow = (rows, idx) => {
    const current = (rows[idx]?.Particular || '').trim();
    if (!current) return false;
    // Check if any subsequent row starts with current particular as prefix
    for (let i = idx + 1; i < rows.length; i++) {
      const next = (rows[i]?.Particular || '').trim();
      if (next.startsWith(current + ' ') || next.startsWith(current + '-') || next.startsWith(current + ' -')) return true;
      // If next row doesn't start with current, stop looking
      if (!next.startsWith(current)) break;
    }
    return false;
  };

  // Sum only non-header rows (leaf/sub-item rows)
  const totalSources = sources.reduce((s, r, i) => {
    if (isHeaderRow(sources, i)) return s;
    return s + (parseFloat(r.Amount) || 0);
  }, 0);

  const totalApplications = applications.reduce((s, r, i) => {
    if (isHeaderRow(applications, i)) return s;
    return s + (parseFloat(r.Amount) || 0);
  }, 0);
  const netFlow = totalSources - totalApplications;
  const isInflow = netFlow >= 0;

  const maxRows = Math.max(sources.length, applications.length);

  const selectedBranchName = selectedBranch !== '0'
    ? (branches.find(b => String(b.Id) === String(selectedBranch))?.Name ||
       branches.find(b => String(b.Id) === String(selectedBranch))?.BranchName || '')
    : '';

  const handleExport = async () => {
    logAudit(AUDIT_ACTION.EXPORT, 'FundFlowStatement', 0);
    if (!data.length) return;
    const rows = [
      ['Sources of Funds', '', 'Application of Funds', ''],
      ['Particulars', 'Amount (₹)', 'Particulars', 'Amount (₹)'],
    ];
    for (let i = 0; i < maxRows; i++) {
      const src = sources[i];
      const app = applications[i];
      rows.push([
        src ? src.Particular : '',
        src ? fmtAmt(src.Amount) : '',
        app ? app.Particular : '',
        app ? fmtAmt(app.Amount) : '',
      ]);
    }
    rows.push([]);
    rows.push(['Total Sources', fmtAmt(totalSources), 'Total Applications', fmtAmt(totalApplications)]);
    if (isInflow) {
      rows.push(['Net Inflow', fmtAmt(netFlow), '', '']);
    } else {
      rows.push(['', '', 'Net Outflow', fmtAmt(Math.abs(netFlow))]);
    }

    await exportStyledExcel({
      reportTitle: 'FUND FLOW STATEMENT',
      sheetName: 'Fund Flow',
      fileName: `FundFlow_${fromDate}_${toDate}.xlsx`,
      companyName,
      username,
      fromDate,
      toDate,
      rawRows: rows,
      columnWidths: [32, 16, 32, 16],
    });
  };

  const cellStyle = (align = 'left', bold = false, color = '#1e293b', bg = '#fff') => ({
    padding: '6px 10px',
    fontSize: 12,
    textAlign: align,
    fontWeight: bold ? 700 : 400,
    color,
    background: bg,
    borderBottom: '1px solid #f1f5f9',
    whiteSpace: 'nowrap',
  });

  const thStyle = (align = 'left', bg = '#f8fafc') => ({
    padding: '7px 10px',
    fontSize: 11,
    textAlign: align,
    fontWeight: 700,
    color: '#64748b',
    background: bg,
    borderBottom: '2px solid #e2e8f0',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  });

  return (
    <div style={{ minHeight: '100vh', background: '#f0f4f8', padding: 12, fontFamily: "'Inter', sans-serif" }}>
      {loading && <LogoLoader message="Loading fund flow data..." />}

      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: `linear-gradient(135deg, ${PRIMARY} 0%, #1565c0 100%)`, gap: 8, flexWrap: 'nowrap', overflowX: 'auto' }}>
          <Tooltip title="Back" arrow>
            <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
              <ArrowBack sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' }}>
            Fund Flow Statement
          </span>
          <div style={{ flex: 1 }} />
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
            style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, background: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark' }} />
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
            style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, background: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark' }} />
          <select
            value={selectedBranch}
            onChange={e => setSelectedBranch(e.target.value)}
            style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, background: 'rgba(255,255,255,0.15)', color: '#fff', colorScheme: 'dark', minWidth: 120 }}
          >
            <option value="0" style={{ background: '#1e40af', color: '#fff' }}>All Branches</option>
            {branches.map(b => (
              <option key={b.Id} value={String(b.Id)} style={{ background: '#1e40af', color: '#fff' }}>
                {b.Name || b.BranchName}
              </option>
            ))}
          </select>
          <Tooltip title="Refresh" arrow>
            <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
              <Refresh sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Excel" arrow>
            <IconButton onClick={handleExport} disabled={!data.length} size="small" sx={iconBtn}>
              <GetApp sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </div>

        {/* Report */}
        <div style={{ padding: 16, overflowX: 'auto' }}>

          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: 12 }}>
            {selectedBranch === '0' ? (
              <div style={{ fontSize: 13, fontWeight: 600, color: '#64748b' }}>All Branches</div>
            ) : selectedBranchName ? (
              <div style={{ fontSize: 13, fontWeight: 600, color: '#1e40af' }}>{selectedBranchName}</div>
            ) : null}
            <div style={{ fontSize: 12, color: '#64748b' }}>
              Fund Flow Statement &nbsp;|&nbsp; {fromDate} to {toDate}
            </div>
          </div>

          {!loading && data.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: 13 }}>
              No data found for the selected period.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '35%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '5px' }} />
                <col style={{ width: '35%' }} />
                <col style={{ width: '15%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th colSpan={2} style={{ ...thStyle('center', '#e8f5e9'), color: '#1b5e20', fontSize: 12, borderBottom: '2px solid #a5d6a7' }}>
                    Sources of Funds
                  </th>
                  <td style={{ background: '#e2e8f0' }} />
                  <th colSpan={2} style={{ ...thStyle('center', '#fce4ec'), color: '#880e4f', fontSize: 12, borderBottom: '2px solid #f48fb1' }}>
                    Application of Funds
                  </th>
                </tr>
                <tr>
                  <th style={thStyle('left', '#f1f8e9')}>Particulars</th>
                  <th style={thStyle('right', '#f1f8e9')}>Amount</th>
                  <td style={{ background: '#e2e8f0' }} />
                  <th style={thStyle('left', '#fce4ec')}>Particulars</th>
                  <th style={thStyle('right', '#fce4ec')}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  return Array.from({ length: maxRows }).map((_, i) => {
                  const src = sources[i];
                  const app = applications[i];

                  // A row is a "header/group" row if it has sub-items below it
                  const srcIsHeader = src ? isHeaderRow(sources, i) : false;
                  const appIsHeader = app ? isHeaderRow(applications, i) : false;

                  // Source side colors
                  const srcBg = src
                    ? srcIsHeader ? '#e8f5e9' : '#f9fffe'
                    : '#fff';
                  const srcTextColor = srcIsHeader ? '#1b5e20' : '#374151';
                  const srcAmtColor = '#1b5e20';

                  // Application side colors
                  const appBg = app
                    ? appIsHeader ? '#fce4ec' : '#fff9fb'
                    : '#fff';
                  const appTextColor = appIsHeader ? '#880e4f' : '#374151';
                  const appAmtColor = '#880e4f';

                  return (
                    <tr key={i}>
                      <td style={{
                        ...cellStyle('left', srcIsHeader, srcTextColor, srcBg),
                        paddingLeft: src && !srcIsHeader ? 22 : 10,
                        fontSize: srcIsHeader ? 12 : 11.5,
                        borderBottom: srcIsHeader ? '1px solid #c8e6c9' : '1px solid #f1f5f9',
                      }}>
                        {src ? src.Particular : ''}
                      </td>
                      <td style={{
                        ...cellStyle('right', false, srcAmtColor, srcBg),
                        borderBottom: srcIsHeader ? '1px solid #c8e6c9' : '1px solid #f1f5f9',
                      }}>
                        {src && (parseFloat(src.Amount) || 0) !== 0 ? fmtAmt(src.Amount) : ''}
                      </td>
                      <td style={{ background: '#e2e8f0', width: 5 }} />
                      <td style={{
                        ...cellStyle('left', appIsHeader, appTextColor, appBg),
                        paddingLeft: app && !appIsHeader ? 22 : 10,
                        fontSize: appIsHeader ? 12 : 11.5,
                        borderBottom: appIsHeader ? '1px solid #f8bbd0' : '1px solid #f1f5f9',
                      }}>
                        {app ? app.Particular : ''}
                      </td>
                      <td style={{
                        ...cellStyle('right', false, appAmtColor, appBg),
                        borderBottom: appIsHeader ? '1px solid #f8bbd0' : '1px solid #f1f5f9',
                      }}>
                        {app && (parseFloat(app.Amount) || 0) !== 0 ? fmtAmt(app.Amount) : ''}
                      </td>
                    </tr>
                  );
                  });
                })()}

                {/* Net Inflow / Outflow row */}
                {isInflow ? (
                  // Sources > Applications → Net Inflow on Sources (left) side
                  <tr style={{ background: '#d1fae5' }}>
                    <td style={cellStyle('right', true, '#065f46', '#d1fae5')}>Net Inflow</td>
                    <td style={cellStyle('right', true, '#065f46', '#d1fae5')}>{fmtAmt(netFlow)}</td>
                    <td style={{ background: '#e2e8f0' }} />
                    <td colSpan={2} style={cellStyle('left', false, '#065f46', '#d1fae5')} />
                  </tr>
                ) : (
                  // Applications > Sources → Net Outflow on Applications (right) side
                  <tr style={{ background: '#fee2e2' }}>
                    <td colSpan={2} style={cellStyle('left', false, '#991b1b', '#fee2e2')} />
                    <td style={{ background: '#e2e8f0' }} />
                    <td style={cellStyle('right', true, '#991b1b', '#fee2e2')}>Net Outflow</td>
                    <td style={cellStyle('right', true, '#991b1b', '#fee2e2')}>{fmtAmt(Math.abs(netFlow))}</td>
                  </tr>
                )}

                {/* Totals — each side shows its own actual total */}
                <tr style={{ background: '#f1f5f9' }}>
                  <td style={cellStyle('right', true, '#1e293b', '#f1f5f9')}>Total Sources</td>
                  <td style={cellStyle('right', true, '#1b5e20', '#f1f5f9')}>
                    {fmtAmt(totalSources)}
                  </td>
                  <td style={{ background: '#e2e8f0' }} />
                  <td style={cellStyle('right', true, '#1e293b', '#f1f5f9')}>Total Applications</td>
                  <td style={cellStyle('right', true, '#880e4f', '#f1f5f9')}>
                    {fmtAmt(totalApplications)}
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
      </Snackbar>
    </div>
  );
};

export default FundFlowStatement;
